import {Card} from '@atoms';
import {Group, GroupMember, useAppSelector} from '@lib/store';
import React, {useMemo} from 'react';
import {ListHeader} from '../../Connections/components';
import {GroupMemberItem} from './GroupMemberItem';
import {Subtitle} from '@quarks';
import {useTranslation} from 'react-i18next';
import {useModal} from '@hooks';
import {GroupMemberSelectionModal} from '../../Connections/components/GroupMemberSelectionModal';

type Props = {};

export const GroupMembersSection = ({group, className}: {group: Group; className?: string}) => {
  const {t} = useTranslation();
  const currentUserId = useAppSelector((state) => state.data.user.user.id);
  const {openModal, addPages} = useModal();

  const invitedByUserId = useMemo(
    () => group?.members.find((member) => member.user.id === currentUserId)?.invitedByUser.id,
    [group, currentUserId],
  );
  const {acceptedMembers, pendingMembers} = useMemo(
    () =>
      [...(group?.members || [])]
        .sort((a, b) => {
          const nameA = a.user.name.toUpperCase();
          const nameB = b.user.name.toUpperCase();

          if (invitedByUserId && invitedByUserId === a.user.id) return -1;
          if (invitedByUserId && invitedByUserId === b.user.id) return 1;

          return nameA.localeCompare(nameB);
        })
        .reduce(
          (acc, member) => {
            if (member.status === 'Accepted') acc.acceptedMembers.push(member);
            if (member.status === 'Pending') acc.pendingMembers.push(member);

            return acc;
          },
          {acceptedMembers: [] as GroupMember[], pendingMembers: [] as GroupMember[]},
        ),
    [group?.members, invitedByUserId],
  );

  return (
    <Card className="flex flex-col py-8 gap-6">
      <ListHeader
        title={t('groups:Members')}
        className="sm:px-4 px-6"
        action={() => {
          addPages([<GroupMemberSelectionModal group={group} />]);
          openModal();
        }}
      />

      <div className="grid divide-y px-6">
        {acceptedMembers.map((member) => (
          <GroupMemberItem
            key={member.user.id}
            groupId={group.id}
            member={member}
          />
        ))}
      </div>

      <Subtitle className="sm:px-4 px-6">{t('groups:PendingMembersLabel')}</Subtitle>
      <div className="grid divide-y px-6">
        {pendingMembers.map((member) => (
          <GroupMemberItem
            key={member.user.id}
            groupId={group.id}
            member={member}
          />
        ))}
      </div>
    </Card>
  );
};
